// import dependencies
import React, { FunctionComponent, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { Checkbox, CommonButton, ConvertStringToReact, TrustArcLogo } from '@common/components'
import {
    VIEW_PATH,
    BillingPeriodKey,
    BillingPeriod,
    BillingPeriodTimePeriodMapper,
    COUPON_DISCOUNT_TYPE,
    PRODUCTS,
} from '@common/constants'
import { getSessionState, saveSessionState, connectRedux, GoToComponentProps } from '@common/utils'

import { BUY_USER_TYPES, ADDONS } from '@buy/constants'
import {
    mapBuyOrderSummaryState,
    mapBuyOrderSummaryActions,
    BuyOrderSummaryActionsType,
} from '@buy/connectedComponents/OrderSummary/connector'
import { ConnectedModal } from '@common/connectedComponents/Modal'
import { BuyOrderSummaryState } from '@buy/connectedComponents/OrderSummary/state'
import { get, has, isEmpty, isUndefined } from 'lodash-es'
import { BaseLineSummary } from '@buy/components/BaseLineSummary/BaseLineSummary'
import { AdditionalLinesSummary } from '@buy/components/AdditionalLinesSummary/AdditionalLinesSummary'
import { AddonsSummaryWrapper } from '@buy/components/AddonsSummaryWrapper'
import { useLocation, useParams } from 'react-router-dom'
import { ParamTypes } from '@buy/Routes'
import { remapPricingObject } from '@buy/utils/volume-components-helpers'
import { SKUSelectorsResponse } from '@buy/components/VolumeComponentWrapper'
import { DiscountSummary } from '@buy/components/DiscountSummary'
import { TotalSummary } from '@buy/components/TotalSummary'
import { getCheckoutSessionState, setCheckoutSessionState, SessionFieldData, SessionKeys } from '@buy/utils'
import { OrderSummaryLine } from '@buy/state/order-summary/order-summary'
import { BillingPeriodSwitch, BillingTenureValue } from '@buy/components/BillingPeriodSwitch'
import { getProductSettingsData } from '@buy/state/selectors'
import { LocationState } from '@common/state/location/state'
import { getLocationState } from '@common/state/selectors'

import styles from './order-summary.module.css'
import paymentStyles from '../PaymentFormInfo/payment-info.module.css'

type BuyOrderSummaryProps = GoToComponentProps<
    BuyOrderSummaryState,
    BuyOrderSummaryActionsType,
    {
        hidePrice?: boolean
        isTabletOrMobileView?: boolean
        isMobileView?: boolean
    }
>

export type addonsContentType = {
    year: string
    month: string
    months: string
    percentageOffPromoDiscount: string
    percentageOffVolumeDiscount: string
    prorateDiscount: string
    totalVolumeDiscount: string
    tooltips: {
        [key: string]: string
    }
}

const BuyOrderSummary: FunctionComponent<BuyOrderSummaryProps> = ({ state, actions, props }) => {
    const { selectedPlan, content, CurrencyCode, disablePlanSelection, couponCode, language, couponServerData } = state
    const productSettings = useSelector(getProductSettingsData)
    const qualtricsProductName = productSettings?.qualtricsProductName
    const { pathProduct, plan } = useParams<ParamTypes>()
    const { pathname } = useLocation()
    const [isCheckout, setIsCheckout] = useState(false)
    const [showAdditionalLines, setShowAdditionalLines] = useState(false)
    const [baselineTotal, setBaselineTotal] = useState<number>(0)
    const [additionalLinesTotal, setAdditionalLinesTotal] = useState<Record<string, number>>({})
    const [addonLinesTotal, setAddonLinesTotal] = useState<number>(0)
    const [isAnnual, setIsAnnual] = useState(false)
    const [rePriceDiscounts, setRePriceDiscounts] = useState<Record<string, number>>({})
    const { locationInfo }: LocationState = useSelector(getLocationState)

    // labels and state display
    const isAddonFlow = state.isUserLoggedIn && state.userType === BUY_USER_TYPES.ADDON

    const billingPeriodTooltips: Record<string, string> = {
        // [BillingPeriodKey.ANNUAL] : content.monthlyTooltip,
        // [BillingPeriodKey.MONTHLY]: content.monthlyTooltip,
        [BillingPeriodKey.ANNUALCONTRACT]: content.monthlyTooltip,
    }

    const addonsContent: addonsContentType = {
        year: content.year,
        month: content.month,
        months: content.months,
        percentageOffPromoDiscount: content.percentageOffPromoDiscount,
        percentageOffVolumeDiscount: content.percentageOffVolumeDiscount,
        prorateDiscount: content.proratedAdjustment,
        totalVolumeDiscount: content.totalVolumeDiscount,
        tooltips: {
            [ADDONS.AGENT]: content.agentsVolumediscountTooltip,
            [ADDONS.ENDPOINT]: content.endpointsVolumediscountTooltip,
            [ADDONS.MOBILE_ADDON]: content.mobileVolumediscountTooltip,
            [ADDONS.CAMERA_SHARE_ADDON]: content.cameraShareVolumediscountTooltip,
            [ADDONS.ENDPOINT_PROTECTION_ADDON]: content.endpointProtectionVolumediscountTooltip,
            proratedadjustmentTooltip: content.proratedadjustmentTooltip,
            totalVolumeDiscountTooltip: content.totalVolumeDiscountTooltip,
        },
    }

    const removeAdditionalLinesSubTotalKey = (unit: string) => {
        delete additionalLinesTotal[unit]
        setAdditionalLinesTotal(additionalLinesTotal)
    }

    const loadSessionStorageLinesSummary = (sessionData: SessionFieldData, key: string) => {
        const hasLines = !isEmpty(sessionData) && !isEmpty(get(sessionData, key))
        if (hasLines) {
            let sessionLines: OrderSummaryLine[] = get(sessionData, key)!
            const skuSelectorSkus = selectedPlan.sKUSelectors!.map((item) => item.sku)
            const legacyAddons = selectedPlan.addons
                ?.filter((item) => has(item, 'sKUSelector'))
                .map((values) => values.sKUSelector)
            sessionLines = sessionLines.filter((item) =>
                [...skuSelectorSkus, ...legacyAddons.map((item2) => item2.sku)].includes(item.key)
            )
            const updatedSessionLines = sessionLines.map((item) => {
                const newItem = { ...item }
                const { pricingInfo, unit, planName, quantityMultiplier, sku } = [
                    ...selectedPlan.sKUSelectors!,
                    ...legacyAddons,
                ].find((item2) => item2.sku === item.key)!
                newItem.key = sku
                if (!isEmpty(unit?.unitName)) {
                    newItem.unit = unit?.unitName
                    newItem.usagePriceLabel = unit?.usagePriceLabel
                }
                newItem.skuName = planName
                const priceObj = remapPricingObject(pricingInfo, CurrencyCode)
                newItem.allPrices = priceObj.allPrices
                newItem.usagePrice = priceObj.usagePrice
                newItem.quantityMultiplier = quantityMultiplier
                return newItem
            })
            return updatedSessionLines
        }
        return null
    }

    const loadSessionStorageLines = () => {
        const sessionData = getCheckoutSessionState(pathProduct!, plan!)
        if (!isEmpty(sessionData)) {
            const sessionAdditionalLines = loadSessionStorageLinesSummary(sessionData, SessionKeys.additonalLines)
            const sessionAddonLines = loadSessionStorageLinesSummary(sessionData, SessionKeys.addonLines)
            if (!isEmpty(sessionAdditionalLines)) actions.setAdditionalLines(sessionAdditionalLines!)
            if (!isEmpty(sessionAddonLines)) actions.setAddonLines(sessionAddonLines!)
        }
    }

    useEffect(() => {
        const qualtricsData = getSessionState('qualtrics_data_gh')
        saveSessionState('qualtrics_data_gh', {
            ...(!qualtricsData ? {} : qualtricsData),
            // id: '', // added on GTM side
            sku: selectedPlan.sKU,
            purchase_term: state.billingTenure.selected,
            purchase_type: isAddonFlow ? 'addon' : 'new',
            order_total: state.finalTotal,
            // referral_channel: '', // added on GTM side
            promo_code_applied: couponCode ?? '',
            site_language: language,
            product: qualtricsProductName,
            currency: locationInfo.currency,
        })
    }, [
        selectedPlan.sKU,
        isAddonFlow,
        state.finalTotal,
        state.billingTenure,
        couponCode,
        language,
        qualtricsProductName,
        locationInfo.currency,
    ])

    useEffect(() => {
        setIsCheckout(pathname.includes(VIEW_PATH.PATH_CHECKOUT))
    }, [pathname])

    useEffect(() => {
        if (!isEmpty(state.selectedPlan.sKUSelectors)) {
            const sessionData = getCheckoutSessionState(pathProduct!, plan!)
            const sessionBasePlanLine = sessionData?.baseLine

            loadSessionStorageLines()
            if (!productSettings.enablePlanCustomization) {
                // this product has no customize page
                const defaultSku = state.selectedPlan.sKUSelectors![0]
                const couponItems =
                    couponServerData?.couponDiscountType === COUPON_DISCOUNT_TYPE.REPRICE
                        ? couponServerData?.couponItems.filter((item) => item.productSku === defaultSku.sku)
                        : []
                const newPrices = remapPricingObject(defaultSku.pricingInfo, CurrencyCode, couponItems)
                setRePriceDiscounts(newPrices.rePriceDiscounts)
                const tempBaseLine: OrderSummaryLine = {
                    skuName: defaultSku.planName,
                    key: defaultSku.sku,
                    multiplier: sessionBasePlanLine ? sessionBasePlanLine.multiplier : 1,
                    quantity: sessionBasePlanLine ? sessionBasePlanLine.quantity : 1,
                    allPrices: newPrices.allPrices,
                    quantityMultiplier: defaultSku.quantityMultiplier,
                }
                actions.setBaseLine(tempBaseLine)
                setCheckoutSessionState(tempBaseLine, SessionKeys.baseLine, pathProduct!, plan!)
            } else {
                const basePlanSku: SKUSelectorsResponse = state.selectedPlan.sKUSelectors!.find((item) =>
                    has(item, 'includedInSku')
                )!
                const hasAdditionalLinesConfig =
                    !isEmpty(basePlanSku) &&
                    state.selectedPlan.sKUSelectors?.filter((item) => item.sku === basePlanSku!.sku).length === 1
                setShowAdditionalLines(hasAdditionalLinesConfig)
                if (hasAdditionalLinesConfig) {
                    const keyToCompare = !isEmpty(basePlanSku) ? basePlanSku.sku : selectedPlan.sKU
                    const couponItems =
                        couponServerData?.couponDiscountType === COUPON_DISCOUNT_TYPE.REPRICE
                            ? couponServerData?.couponItems.filter((item) => item.productSku === keyToCompare)
                            : []
                    const newPrices = remapPricingObject(
                        !isEmpty(basePlanSku) ? basePlanSku.pricingInfo : selectedPlan.pricingInfo,
                        CurrencyCode,
                        couponItems
                    )
                    setRePriceDiscounts(newPrices.rePriceDiscounts)
                    const tempBaseLine: OrderSummaryLine = {
                        skuName: !isEmpty(basePlanSku) ? basePlanSku.planName : selectedPlan.name,
                        key: !isEmpty(basePlanSku) ? basePlanSku.sku : selectedPlan.sKU,
                        multiplier: sessionBasePlanLine ? sessionBasePlanLine.multiplier : 1,
                        quantity: sessionBasePlanLine ? sessionBasePlanLine.quantity : 1,
                        allPrices: newPrices.allPrices,
                        quantityMultiplier: !isEmpty(basePlanSku) ? basePlanSku.quantityMultiplier : '',
                    }
                    actions.setBaseLine(tempBaseLine)
                    setCheckoutSessionState(tempBaseLine, SessionKeys.baseLine, pathProduct!, plan!)
                } else {
                    const volumeSelectorSku: SKUSelectorsResponse = state.selectedPlan.sKUSelectors!.find((item) =>
                        has(item, 'volumeSelector')
                    )!
                    const couponItems =
                        couponServerData?.couponDiscountType === COUPON_DISCOUNT_TYPE.REPRICE
                            ? couponServerData?.couponItems.filter((item) => item.productSku === volumeSelectorSku.sku)
                            : []
                    const newPrices = remapPricingObject(volumeSelectorSku.pricingInfo, CurrencyCode, couponItems)
                    setRePriceDiscounts(newPrices.rePriceDiscounts)

                    const defaultBundleSize = isUndefined(volumeSelectorSku.volumeSelector!.quantityPerUnit)
                        ? volumeSelectorSku.volumeSelector!.minimum!
                        : volumeSelectorSku.volumeSelector!.minimum! /
                          volumeSelectorSku.volumeSelector!.quantityPerUnit!

                    const tempBaseLine: OrderSummaryLine = {
                        skuName: volumeSelectorSku.planName,
                        key: volumeSelectorSku.sku,
                        multiplier: sessionBasePlanLine ? sessionBasePlanLine.multiplier : 1,
                        quantity: sessionBasePlanLine ? sessionBasePlanLine.multiplier : defaultBundleSize,
                        unit: !isEmpty(basePlanSku.unit) ? basePlanSku.unit.unitName : volumeSelectorSku.unit?.unitName,
                        allPrices: newPrices.allPrices,
                        quantityMultiplier: !isEmpty(basePlanSku.unit)
                            ? basePlanSku.quantityMultiplier
                            : volumeSelectorSku.quantityMultiplier,
                    }
                    actions.setBaseLine(tempBaseLine)
                    setCheckoutSessionState(tempBaseLine, SessionKeys.baseLine, pathProduct!, plan!)
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        productSettings.enablePlanCustomization,
        isCheckout,
        state.selectedPlan.sKUSelectors,
        CurrencyCode,
        couponServerData,
    ])

    useEffect(() => {
        setIsAnnual(state.billingTenure.selected === BillingPeriod.ANNUAL)
    }, [state.billingTenure])

    return (
        <div>
            <div className={styles.wrapper}>
                <h2 className={styles.header}>{content.orderSummary}</h2>
                <BillingPeriodSwitch
                    onChangeFn={(value: BillingTenureValue) => {
                        actions.setBillingFrequency(BillingPeriodTimePeriodMapper[value.selected])
                    }}
                    disabled={isAddonFlow}
                    id={`toggle-${Math.random()}`}
                    isLarge
                    billingTenure={state.billingTenure}
                    setBillingTenure={actions.setBillingTenure}
                    tooltipContent={billingPeriodTooltips}
                    billingPeriods={state.selectedPlan.billingPeriods}
                    baseLine={state.baseLine}
                    theme={PRODUCTS.RESOLVE}
                />
                {!disablePlanSelection && (
                    <BaseLineSummary
                        baseLine={state.baseLine}
                        planName={state.selectedPlan.name}
                        shortDescription={state.selectedPlan.shortDescription}
                        monthsText={content.months}
                        isAnnual={isAnnual}
                        hasAdditionalLines={false}
                        setSubTotal={setBaselineTotal}
                        subTotal={baselineTotal}
                        billingTenure={state.billingTenure}
                    />
                )}
                {state.additionalLines.map((item) => (
                    <div className={styles.section}>
                        <AdditionalLinesSummary
                            line={item}
                            isAnnual={isAnnual}
                            setSubTotal={(value: number) =>
                                setAdditionalLinesTotal((oldState: Record<string, number>) => {
                                    const newState = { ...oldState }
                                    newState[item.unit!] = value
                                    return { ...newState }
                                })
                            }
                            subTotal={additionalLinesTotal[item.unit!]}
                            cleanUpFunc={removeAdditionalLinesSubTotalKey}
                            billingTenure={state.billingTenure}
                        />
                    </div>
                ))}
                <AddonsSummaryWrapper
                    lines={state.addonLines}
                    isAnnual={isAnnual}
                    mainTitle={content.addOns}
                    baseLine={state.baseLine}
                    setSubTotal={setAddonLinesTotal}
                    subTotal={addonLinesTotal}
                    billingTenure={state.billingTenure}
                />

                <DiscountSummary
                    title={content.discount}
                    annualDiscountLabel={content.percentageOffAnnualDiscount}
                    addonsLabels={addonsContent}
                    isAnnual={isAnnual}
                    baseLine={state.baseLine}
                    additionalLines={state.additionalLines}
                    addonLines={state.addonLines}
                    billingPeriodKeys={state.billingPeriodKeys}
                    showAdditionalLines={showAdditionalLines}
                    percentageOffPromoDiscountLabel={content.percentageOffPromoDiscount}
                    setTotalDiscount={actions.setTotalDiscount}
                    totalDiscount={state.totalDiscount}
                    promoDiscountForAnnualSavingsTooltipLabel={content.promoDiscountForAnnualSavingsTooltip}
                    couponServerData={couponServerData}
                    billingTenure={state.billingTenure}
                    currency={CurrencyCode}
                    validCoupons={state.validCoupons}
                    setValidCoupons={actions.setValidCoupons}
                    setCouponValidAndApplied={actions.setCouponValidAndApplied}
                    rePriceDiscounts={rePriceDiscounts}
                />

                <TotalSummary
                    subTotalLabel={content.subtotal}
                    estimatedTaxLabel={content.estimatedTax}
                    estimatedTaxPopupInfoLabel={content.estimatedTaxPopupInfo}
                    tBDLabel={content.tBD}
                    totalLabel={content.total}
                    calculatingLabel={content.calculating}
                    annualPlanNoteLabel={content.annualPlanNote}
                    monthlyPlanNoteLabel={content.monthlyPlanNote}
                    totalLoading={state.totalLoading}
                    calculatedTax={state.calculatedTax}
                    currencyCode={CurrencyCode}
                    baseLineTotal={baselineTotal}
                    additionalLinesTotal={additionalLinesTotal}
                    addonLinesTotal={addonLinesTotal}
                    totalDiscount={state.totalDiscount}
                    additionalLines={state.additionalLines}
                    setFinalTotal={actions.setFinalTotal}
                    finalTotal={state.finalTotal}
                    billingTenure={state.billingTenure}
                    annualContractPlanNoteLabel={content.annualConractPlanNote}
                    annualContractTotalLabel={content.annualContractTotal}
                />
            </div>
            <div className={`${paymentStyles.terms}`}>
                <ConvertStringToReact htmlString={content.pricingNote} />{' '}
                <button
                    className={paymentStyles['optout-button']}
                    onClick={() => actions.setDisplayOptOut(!state.displayOptOut)}
                    type="button"
                >
                    {content.optOut}
                </button>
                {state.displayOptOut && (
                    <Checkbox
                        label={content.optOutCheckboxLabel}
                        modifier={paymentStyles['optout-checkbox']}
                        checked={state.isUserOptOut}
                        onchange={() => actions.setIsUserOptOut(!state.isUserOptOut)}
                        qaTag="checkout-form-optout"
                    />
                )}
            </div>
            {!props.isTabletOrMobileView && <TrustArcLogo label={content.trustArcSupport} />}
            <ConnectedModal isOpen={state.modal.isModalOpen} closeable onClose={() => actions.closeErrorModal()}>
                <div data-qat="negative-total-restriction-modal">
                    <h3>{state.content.upgradeCreditErrorMessage}</h3>
                    <div>
                        <ConvertStringToReact htmlString={state.content.upgradeCreditErrorAssistance} />
                    </div>
                    <CommonButton onClick={() => actions.closeErrorModal()}>{state.content.upgradeClose}</CommonButton>
                </div>
            </ConnectedModal>
        </div>
    )
}

export const ConnectedBuyOrderSummary = connectRedux(
    BuyOrderSummary,
    mapBuyOrderSummaryState,
    mapBuyOrderSummaryActions
)

export default ConnectedBuyOrderSummary
